import {
  memo,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  withRepeat,
  withTiming,
  useSharedValue,
  useAnimatedStyle,
  useAnimatedReaction,
  Easing,
  interpolate,
} from 'react-native-reanimated';
import { runOnJS } from 'react-native-worklets';
import { useIsFocused } from '@bonanza-social/navigation';
import { Box, useLayout, useStyle, ViewStyle } from '@bonanza-social/moonlight';
import { getGameOffset } from '../../GamesRandomizer.utils';
import { VISIBLE_RANGE, IMAGES_GAP_TOKEN } from '../../GamesRandomizer.content';
import {
  GamesRandomizerColumnProps,
  GamesRandomizerColumnExposedProps,
} from './GamesRandomizerColumn.types';
import { Game } from '../../../../state/shared/games.types';
import Thumbnail from '../../../Games/Game/Thumbnail/Thumbnail';

function GamesRandomizerColumn(
  {
    gameIds,
    gamesHeight,
    targetGameId,
    itemSize,
    isEven,
  }: GamesRandomizerColumnProps,
  ref: ForwardedRef<GamesRandomizerColumnExposedProps>,
) {
  const [layout, onLayout] = useLayout();
  const [visibleIndex, setVisibleIndex] = useState(0);
  const focused = useIsFocused();
  const opacity = useSharedValue(1);
  const translateY = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(
    () => ({ transform: [{ translateY: translateY.value }] }),
    [translateY],
  );
  const fadeStyle = useAnimatedStyle(
    () => ({ opacity: opacity.value }),
    [opacity],
  );
  const tripleGameIds = useMemo(
    () => [...gameIds, ...gameIds, ...gameIds],
    [gameIds],
  );
  const { gap } = useStyle<ViewStyle>({ gap: IMAGES_GAP_TOKEN });

  const onAnimatedReaction = useCallback(
    (value: number) => {
      if (!focused) {
        return;
      }

      const absValue = Math.abs(value);
      const maxOffset = gamesHeight * 3 - layout.height;
      const visibleIndex = Math.round(
        interpolate(absValue, [0, maxOffset], [0, tripleGameIds.length - 1]),
      );

      setVisibleIndex(visibleIndex);
    },
    [focused, gamesHeight, layout.height, tripleGameIds.length],
  );

  const applyDefaultAnimation = useCallback(() => {
    const remainingOffset = gamesHeight * 2 + translateY.value;
    const duration = gamesHeight * 150 * (isEven ? 0.5 : 1);
    const remainingDuration = (remainingOffset / (gamesHeight * 2)) * duration;

    translateY.value = withRepeat(
      withTiming(-gamesHeight * 2, {
        duration: remainingDuration,
        easing: Easing.linear,
      }),
      -1,
    );
  }, [gamesHeight, translateY, isEven]);

  const onChoosingStart = useCallback(
    (randomId: Game['id'], targetOffset: number, onGameChoosed: () => void) => {
      requestAnimationFrame(() => {
        const delta =
          targetOffset > 4000 ? targetOffset : targetOffset + gamesHeight;
        const gameHeight = gamesHeight / gameIds.length;

        translateY.value = withTiming(
          -delta + (isEven ? gameHeight * 0.3 : 0),
          {
            duration: 6000,
            easing: Easing.out(Easing.ease),
          },
          () => {
            opacity.value = withTiming(0.2);

            if (gameIds.includes(randomId)) {
              runOnJS(onGameChoosed)();
            }
          },
        );
      });
    },
    [gamesHeight, gameIds, translateY, isEven, opacity],
  );

  const onChoosingStop = useCallback(() => {
    translateY.value = Math.min(0, translateY.value + gamesHeight * 2); // Handle columns count change while spining
    opacity.value = 1;

    applyDefaultAnimation();
  }, [gamesHeight, translateY, opacity, applyDefaultAnimation]);

  useEffect(applyDefaultAnimation, [applyDefaultAnimation]);

  useAnimatedReaction(
    () => translateY.value,
    (value) => {
      runOnJS(onAnimatedReaction)(value);
    },
  );

  useImperativeHandle(
    ref,
    () => ({
      onChoosingStart,
      onChoosingStop,
    }),
    [onChoosingStart, onChoosingStop],
  );

  return (
    <Box flex={1} animatedStyle={animatedStyle} onLayout={onLayout}>
      {VISIBLE_RANGE.map((number) => {
        const index = visibleIndex + number;
        const id = tripleGameIds[index];

        return (
          <Box
            key={id ?? index}
            position="absolute"
            left="space-0"
            right="space-0"
            transform={`translateY(${getGameOffset(+gap!, index, itemSize)}px)`}
            animatedStyle={id === targetGameId ? undefined : fadeStyle}
            borderRadius="radius-400"
            overflow="hidden"
          >
            <Thumbnail gameId={id} type="square" dynamicSize />
          </Box>
        );
      })}
    </Box>
  );
}

export default memo(forwardRef(GamesRandomizerColumn));
